<template>
  <div class="sli-admin-grid">
    <div class="sli-cell sli-cell--gov">
      <label class="sli-cell__label">المحافظة</label>
      <v-select
        :id="`${uid}-gov`"
        v-model="selectedGovernorate"
        :options="governorateOptions"
        :get-option-label="optionLabel"
        :dir="'rtl'"
        :clearable="!disabled"
        :loading="loading.governorate"
        :disabled="disabled"
        placeholder="المحافظة"
        class="sli-mini-select"
        @input="onGovernorate"
      />
    </div>
    <div
      class="sli-cell sli-cell--zone"
      style="--sli-fit-ch: 8"
    >
      <label
        class="sli-cell__label"
        :for="`${uid}-zone`"
      >المنطقة</label>
      <v-select
        :id="`${uid}-zone`"
        v-model="land.zone"
        :options="zoneChoices"
        :dir="'rtl'"
        :clearable="!disabled"
        :filterable="true"
        :taggable="!disabled"
        :disabled="disabled"
        placeholder="منطقة"
        class="sli-mini-select"
      />
    </div>
    <div class="sli-cell sli-cell--center">
      <label class="sli-cell__label">المركز</label>
      <v-select
        :id="`${uid}-center`"
        v-model="selectedCenter"
        :options="centerOptions"
        :get-option-label="optionLabel"
        :dir="'rtl'"
        :clearable="!disabled"
        :loading="loading.center"
        :disabled="disabled || (!land.governorate_id && !land.governorate)"
        placeholder="المركز"
        class="sli-mini-select"
        @input="onCenter"
      />
    </div>
    <div class="sli-cell sli-cell--village">
      <label class="sli-cell__label">القرية/الناحية</label>
      <v-select
        :id="`${uid}-village`"
        v-model="selectedVillage"
        :options="villageOptions"
        :get-option-label="optionLabel"
        :create-option="createNamedOption"
        :dir="'rtl'"
        :clearable="!disabled"
        :filterable="true"
        :taggable="!disabled"
        :disabled="disabled"
        :loading="loading.village"
        placeholder="اختر أو اكتب القرية/الناحية"
        class="sli-mini-select"
        @input="onVillage"
        @search="villageSearch = $event || ''"
        @search:blur="onVillageBlur"
      >
        <template #no-options="{ search }">
          <span v-if="search">اضغط Enter لإضافة "{{ search }}"</span>
          <span v-else>لا توجد قرى — اكتب اسم القرية لإضافتها</span>
        </template>
      </v-select>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import governoratesApi from '@/api/settings/governorates'
import centersApi from '@/api/settings/centers'
import villagesApi from '@/api/settings/villages'
import { optionLabel, findNamedOption } from '../formModel'

function unwrapDropdown(response) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.data)) return response.data.data
  return []
}

export default {
  name: 'SliAdminFields',
  components: { vSelect },
  props: {
    land: { type: Object, required: true },
    uid: { type: String, default: 'loc' },
    zoneOptions: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      governorateOptions: [],
      centerOptions: [],
      villageOptions: [],
      selectedGovernorate: null,
      selectedCenter: null,
      selectedVillage: null,
      villageSearch: '',
      loading: {
        governorate: false,
        center: false,
        village: false,
      },
      hydrating: false,
    }
  },
  computed: {
    zoneChoices() {
      const current = String(this.land.zone || '').trim()
      const list = (this.zoneOptions || []).slice()
      if (current && !list.includes(current)) list.unshift(current)
      return list
    },
  },
  watch: {
    'land._key'() {
      this.resetAndHydrate()
    },
  },
  mounted() {
    this.loadGovernorates().then(() => this.hydrateFromLand())
  },
  methods: {
    optionLabel,
    findOption(options, id, name) {
      return findNamedOption(options, id, name)
    },
    ensureOption(listKey, option, id, name) {
      if (option && typeof option === 'object') return option
      const label = typeof option === 'string' ? option : name
      if (!label) return option || null
      const synthetic = { id: id || null, name_ar: label, name: label }
      const list = this[listKey] || []
      const exists = list.some(item => (
        (synthetic.id && String(item.id) === String(synthetic.id))
        || optionLabel(item) === label
      ))
      if (!exists) this[listKey] = [synthetic].concat(list)
      return synthetic
    },
    resetAndHydrate() {
      this.selectedGovernorate = null
      this.selectedCenter = null
      this.selectedVillage = null
      this.centerOptions = []
      this.villageOptions = []
      this.hydrateFromLand()
    },
    hydrateFromLand() {
      this.hydrating = true
      this.selectedGovernorate = this.ensureOption(
        'governorateOptions',
        this.findOption(this.governorateOptions, this.land.governorate_id, this.land.governorate),
        this.land.governorate_id,
        this.land.governorate,
      )
      if (this.selectedGovernorate && typeof this.selectedGovernorate === 'object') {
        this.land.governorate_id = this.selectedGovernorate.id || this.land.governorate_id
        this.land.governorate = optionLabel(this.selectedGovernorate) || this.land.governorate
      }
      const tasks = []
      if (this.land.governorate_id) tasks.push(this.loadCenters(this.land.governorate_id))
      Promise.all(tasks)
        .then(() => {
          this.selectedCenter = this.ensureOption(
            'centerOptions',
            this.findOption(this.centerOptions, this.land.center_id, this.land.center),
            this.land.center_id,
            this.land.center,
          )
          const params = this.land.center_id
            ? { center_id: this.land.center_id }
            : (this.land.governorate_id ? { governorate_id: this.land.governorate_id } : null)
          return params ? this.loadVillages(params) : null
        })
        .then(() => {
          this.selectedVillage = this.ensureOption(
            'villageOptions',
            this.findOption(
              this.villageOptions,
              this.land.village_id || this.land.nahia_id,
              this.land.village || this.land.nahia,
            ) || this.land.village || this.land.nahia || null,
            this.land.village_id || this.land.nahia_id,
            this.land.village || this.land.nahia,
          )
          if (this.selectedVillage) {
            const { id, name } = this.assignName(this.selectedVillage)
            this.land.village_id = this.land.village_id || id
            this.land.village = this.land.village || name
            this.land.nahia_id = this.land.nahia_id || this.land.village_id
            this.land.nahia = this.land.nahia || this.land.village
          }
        })
        .finally(() => {
          this.hydrating = false
        })
    },
    loadGovernorates() {
      this.loading.governorate = true
      return governoratesApi.dropdown()
        .then(res => { this.governorateOptions = unwrapDropdown(res) })
        .finally(() => { this.loading.governorate = false })
    },
    loadCenters(governorateId) {
      this.loading.center = true
      return centersApi.dropdown({ governorate_id: governorateId })
        .then(res => { this.centerOptions = unwrapDropdown(res) })
        .finally(() => { this.loading.center = false })
    },
    loadVillages(params) {
      this.loading.village = true
      return villagesApi.dropdown(params)
        .then(res => { this.villageOptions = unwrapDropdown(res) })
        .catch(() => { this.villageOptions = [] })
        .finally(() => { this.loading.village = false })
    },
    assignName(option) {
      if (!option) return { id: null, name: '' }
      if (typeof option === 'string') return { id: null, name: option }
      return { id: option.id || null, name: optionLabel(option) }
    },
    onGovernorate(option) {
      if (this.hydrating) return
      const { id, name } = this.assignName(option)
      this.land.governorate_id = id
      this.land.governorate = name
      this.selectedCenter = null
      this.selectedVillage = null
      this.land.center = ''
      this.land.center_id = null
      this.land.village = ''
      this.land.village_id = null
      this.land.nahia = ''
      this.land.nahia_id = null
      this.centerOptions = []
      this.villageOptions = []
      if (this.land.governorate_id) this.loadCenters(this.land.governorate_id)
      this.$emit('identity-change')
    },
    onCenter(option) {
      if (this.hydrating) return
      const { id, name } = this.assignName(option)
      this.land.center_id = id
      this.land.center = name
      this.selectedVillage = null
      this.land.village = ''
      this.land.village_id = null
      this.land.nahia = ''
      this.land.nahia_id = null
      this.villageOptions = []
      if (this.land.center_id) {
        this.loadVillages({ center_id: this.land.center_id, governorate_id: this.land.governorate_id })
      }
      this.$emit('identity-change')
    },
    createNamedOption(text) {
      const name = String(text || '').trim()
      return { id: null, name_ar: name, name }
    },
    onVillageBlur() {
      const text = String(this.villageSearch || '').trim()
      this.villageSearch = ''
      if (!text || this.disabled) return
      if (this.selectedVillage && optionLabel(this.selectedVillage) === text) return
      const match = this.findOption(this.villageOptions, null, text)
      this.selectedVillage = match && typeof match === 'object' ? match : this.createNamedOption(text)
      this.onVillage(this.selectedVillage)
    },
    onVillage(option) {
      if (this.hydrating) return
      const { id, name } = this.assignName(option)
      this.land.village_id = id
      this.land.village = name
      this.land.nahia_id = id
      this.land.nahia = name
      this.$emit('identity-change')
    },
  },
}
</script>
